import React from "react";

import { useAuthState } from "src/features/auth/hooks/useAuthState";
import { useKeepAlive } from "src/features/auth/hooks/useKeepAlive";
import { useSignOut } from "src/features/auth/hooks/useSignOut";
import {
	CONFIRMED_SIGN_OUT_KEY,
	clearSessionTimestamp,
	isConfirmedSignOutEvent,
	LAST_ACTIVITY_KEY,
	LAST_KEEP_ALIVE_KEY,
	readSessionTimestamp,
	redirectToSignIn,
	writeSessionTimestamp
} from "src/features/auth/session";

export function useAutoSignOut(): void {
	const { current: service } = React.useRef(new AutoSignOutService());

	const { isInitialized, isAuthenticated } = useAuthState();
	const keepAlive = useKeepAlive();
	const signOut = useSignOut({ notifyOnFailure: false });

	React.useEffect(() => {
		service.setKeepAlive(keepAlive);
	}, [service, keepAlive]);

	React.useEffect(() => {
		service.setSignOut(signOut);
	}, [service, signOut]);

	React.useEffect(() => {
		if (isInitialized) {
			if (isAuthenticated) {
				service.setup();
			} else {
				service.teardown();
			}
		}
		return () => {
			service.teardown();
		};
	}, [service, isInitialized, isAuthenticated]);
}

const ONE_MINUTE = 60 * 1000;
const TWO_HOURS = 120 * ONE_MINUTE;

export const CHECK_INTERVAL = ONE_MINUTE;
export const KEEP_ALIVE_INTERVAL = 10 * ONE_MINUTE;
export const SIGN_OUT_TIMEOUT = TWO_HOURS;
export const ACTIVITY_WRITE_INTERVAL = 15 * 1000;

const ACTIVITY_EVENTS = ["pointermove", "pointerdown", "keydown"];

type AsyncFn = () => unknown;

export class AutoSignOutService {
	private keepAlive: AsyncFn = () => {};
	private signOut: AsyncFn = () => {};
	private interval: number | undefined;
	private isSetup = false;
	private keepAliveInFlight = false;
	private signOutInFlight = false;
	private signOutPending = false;
	private lastActivityWrite = 0;

	constructor(
		private readonly onConfirmedSignOut: () => void = redirectToSignIn
	) {}

	setKeepAlive(keepAlive: AsyncFn): void {
		this.keepAlive = keepAlive;
	}

	setSignOut(signOut: AsyncFn): void {
		this.signOut = signOut;
	}

	public setup(): void {
		if (this.isSetup) {
			return;
		}
		this.isSetup = true;

		const now = Date.now();
		writeSessionTimestamp(LAST_ACTIVITY_KEY, now);
		this.lastActivityWrite = now;
		this.getLastKeepAlive(now);
		clearSessionTimestamp(CONFIRMED_SIGN_OUT_KEY);

		this.addEventListeners();
		this.setInterval();
		this.check();
	}

	public teardown(): void {
		if (!this.isSetup) {
			return;
		}
		this.isSetup = false;
		this.removeEventListeners();
		this.clearInterval();
	}

	private addEventListeners(): void {
		for (const event of ACTIVITY_EVENTS) {
			document.addEventListener(event, this.onActivity, false);
		}
		document.addEventListener("scroll", this.onActivity, true);
		document.addEventListener(
			"visibilitychange",
			this.onVisibilityChange,
			false
		);
		window.addEventListener("focus", this.onResume, false);
		window.addEventListener("pageshow", this.onResume, false);
		window.addEventListener("storage", this.onStorage, false);
	}

	private removeEventListeners(): void {
		for (const event of ACTIVITY_EVENTS) {
			document.removeEventListener(event, this.onActivity, false);
		}
		document.removeEventListener("scroll", this.onActivity, true);
		document.removeEventListener(
			"visibilitychange",
			this.onVisibilityChange,
			false
		);
		window.removeEventListener("focus", this.onResume, false);
		window.removeEventListener("pageshow", this.onResume, false);
		window.removeEventListener("storage", this.onStorage, false);
	}

	private onActivity = (): void => {
		const now = Date.now();
		if (
			this.signOutPending ||
			now - this.lastActivityWrite < ACTIVITY_WRITE_INTERVAL
		) {
			return;
		}
		const isExpired = this.check();
		if (!isExpired) {
			this.recordActivity(now, true);
		}
	};

	private onVisibilityChange = (): void => {
		if (document.visibilityState === "visible") {
			this.onResume();
		}
	};

	private onResume = (): void => {
		const isExpired = this.check();
		if (!isExpired) {
			this.recordActivity(Date.now(), true);
		}
	};

	private onStorage = (event: StorageEvent): void => {
		if (isConfirmedSignOutEvent(event)) {
			this.onConfirmedSignOut();
		}
	};

	private recordActivity(now: number, force = false): void {
		if (this.signOutPending) {
			return;
		}
		if (force || now - this.lastActivityWrite >= ACTIVITY_WRITE_INTERVAL) {
			writeSessionTimestamp(LAST_ACTIVITY_KEY, now);
			this.lastActivityWrite = now;
		}
	}

	private setInterval(): void {
		this.interval = window.setInterval(this.check, CHECK_INTERVAL);
	}

	private clearInterval(): void {
		if (this.interval !== undefined) {
			window.clearInterval(this.interval);
			this.interval = undefined;
		}
	}

	public check = (): boolean => {
		if (this.signOutPending) {
			this.requestSignOut();
			return true;
		}

		const now = Date.now();
		const lastActivity = this.getLastActivity(now);
		if (now - lastActivity >= SIGN_OUT_TIMEOUT) {
			this.signOutPending = true;
			this.requestSignOut();
			return true;
		}

		const lastKeepAlive = this.getLastKeepAlive(now);
		if (now - lastKeepAlive >= KEEP_ALIVE_INTERVAL) {
			this.requestKeepAlive(now);
		}
		return false;
	};

	private getLastActivity(now: number): number {
		const lastActivity = readSessionTimestamp(LAST_ACTIVITY_KEY, now);
		if (lastActivity !== undefined) {
			return lastActivity;
		}
		writeSessionTimestamp(LAST_ACTIVITY_KEY, now);
		this.lastActivityWrite = now;
		return now;
	}

	private getLastKeepAlive(now: number): number {
		const lastKeepAlive = readSessionTimestamp(LAST_KEEP_ALIVE_KEY, now);
		if (lastKeepAlive !== undefined) {
			return lastKeepAlive;
		}
		const overdue = Math.max(0, now - KEEP_ALIVE_INTERVAL);
		writeSessionTimestamp(LAST_KEEP_ALIVE_KEY, overdue);
		return overdue;
	}

	private requestKeepAlive(now: number): void {
		if (this.keepAliveInFlight) {
			return;
		}
		this.keepAliveInFlight = true;
		writeSessionTimestamp(LAST_KEEP_ALIVE_KEY, now);
		void Promise.resolve()
			.then(this.keepAlive)
			.catch(() => undefined)
			.finally(() => {
				this.keepAliveInFlight = false;
			});
	}

	private requestSignOut(): void {
		if (this.signOutInFlight) {
			return;
		}
		this.signOutInFlight = true;
		void Promise.resolve()
			.then(this.signOut)
			.catch(() => undefined)
			.finally(() => {
				this.signOutInFlight = false;
			});
	}
}
